/**
 * FWA tax invoice: drop-in jsPDF template (jsPDF 2.5.x, the same library the portal already uses).
 *
 *   import { jsPDF } from "jspdf";
 *   import { buildFwaInvoice, roundInvoice } from "./fwa-invoice.js";
 *   const doc = buildFwaInvoice(jsPDF, order, { logoDataUrl });   // returns a jsPDF document
 *   doc.save(`Invoice-${order.number}.pdf`);                        // or doc.output("blob") to email
 *
 * ROUNDING RULE (use the same function for the PDF, the portal screens and the Xero post):
 *   1. unit price rounded to 4 decimals FIRST
 *   2. line total = qty x rounded unit price, rounded to the cent
 *   3. GST per line (10%), rounded to the cent, then summed (this is how Xero calculates it)
 * Post to Xero with ?unitdp=4 and send UnitAmount = the rounded 4dp unit price, LineAmountTypes "Exclusive".
 * Then the PDF, the portal and Xero agree to the cent.
 */

/** Round half away from zero to `dp` decimals, using integer maths to avoid floating point drift. */
export function round(n, dp) {
  const f = 10 ** dp;
  const x = Math.round(Math.abs(n) * f + 1e-9) / f;
  return n < 0 ? -x : x;
}

/**
 * Apply the rounding rule to an order. Lines with qty 0 are dropped (a zero line is not part of the order).
 * @param {{lines:{code:string,description:string,qty:number,unitPrice:number}[], freight?:number, gstRate?:number}} o
 */
export function roundInvoice(o) {
  const rate = o.gstRate ?? 0.1;
  const lines = o.lines.filter((l) => Number(l.qty) > 0).map((l) => {
    const unit = round(Number(l.unitPrice), 4);
    const total = round(Number(l.qty) * unit, 2);
    return { ...l, qty: Number(l.qty), unit, total, gst: round(total * rate, 2) };
  });
  const freight = round(Number(o.freight ?? 0), 2);
  const freightGst = round(freight * rate, 2);
  const goods = round(lines.reduce((t, l) => t + l.total, 0), 2);
  const subtotal = round(goods + freight, 2);
  const gst = round(lines.reduce((t, l) => t + l.gst, 0) + freightGst, 2);
  return { lines, goods, freight, subtotal, gst, total: round(subtotal + gst, 2) };
}

const ORANGE = [247, 60, 0], NAVY = [7, 48, 98], DARK = [26, 26, 26], GRAY = [85, 85, 85], LIGHT = [242, 242, 240], RULE = [210, 210, 205];
const money = (n) => "$" + n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Unit prices: 2 decimals when that's exact, otherwise up to 4 (e.g. $0.0764, $1.25, $13.50). */
const unitMoney = (n) => {
  const s2 = round(n, 2);
  if (Math.abs(s2 - n) < 1e-9) return money(n);
  return "$" + n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};
/** "aus coast energy" → "Aus Coast Energy"; leaves words that are already capitalised or acronyms alone. */
export const tidy = (s) => (s ?? "").replace(/\b([a-z])([a-z]*)\b/g, (_, a, b) => a.toUpperCase() + b);

/**
 * @param {any} jsPDF  the jsPDF constructor
 * @param {object} o   the order / invoice
 *   number, date, dueDate, poNumber, orderNumber?, paidByCard (bool), terms ("30 days from end of month"),
 *   billTo: {company, contact?, address[], email?, phone?}, deliverTo?: {name?, address[]},
 *   lines: [{code, description, qty, unitPrice}], freight (always shown, even 0),
 *   company: {tradingName, legalName, abn, phone, email, website},
 *   bank: {accountName, bank, bsb, account, remittanceEmail}, termsUrl
 * @param {{logoDataUrl?: string}} opts  PNG/JPEG data URL of the horizontal logo
 */
export function buildFwaInvoice(jsPDF, o, opts = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight(), M = 40, R = W - M;
  const t = roundInvoice(o);
  const c = o.company;
  let page = 1;
  const color = (rgb) => doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  const font = (style, size) => { doc.setFont("helvetica", style); doc.setFontSize(size); };

  const footer = () => {
    font("normal", 7.5); color(GRAY);
    doc.text(`Prices in AUD. Subject to ${c.tradingName}'s Terms and Conditions of Trade: ${o.termsUrl}`, W / 2, H - 34, { align: "center" });
    doc.text(`${c.legalName} T/As ${c.tradingName} · ABN ${c.abn}`, W / 2, H - 24, { align: "center" });
  };

  const header = () => {
    doc.setFillColor(...ORANGE); doc.rect(0, 0, W, 6, "F");
    if (opts.logoDataUrl) doc.addImage(opts.logoDataUrl, "PNG", M, 24, 190, 19.4);
    font("normal", 8); color(GRAY);
    doc.text(`${c.legalName} T/As ${c.tradingName}`, M, 56);
    doc.text(`ABN ${c.abn}  ·  ${c.phone}`, M, 66);
    doc.text(`${c.email}  ·  ${c.website}`, M, 76);
    font("bold", 20); color(DARK); doc.text("TAX INVOICE", R, 38, { align: "right" });
    const meta = [["Invoice", o.number], ["Date", o.date], ["Due", o.paidByCard ? "Paid" : o.dueDate], ["Your PO", o.poNumber || "—"]];
    font("normal", 8.5);
    meta.forEach(([k, v], i) => { color(GRAY); doc.text(k, R - 110, 54 + i * 11, { align: "right" }); color(DARK); doc.text(String(v), R, 54 + i * 11, { align: "right" }); });
    doc.setDrawColor(...RULE); doc.line(M, 104, R, 104);
  };

  const block = (x, y, title, rows, w) => {
    font("bold", 7.5); color(ORANGE); doc.text(title.toUpperCase(), x, y);
    font("normal", 9); color(DARK);
    let yy = y + 12;
    rows.filter(Boolean).forEach((r) => { const parts = doc.splitTextToSize(r, w); doc.text(parts, x, yy); yy += parts.length * 11; });
    return yy;
  };

  const cols = [
    { k: "#", x: M + 6, w: 18 },
    { k: "Description", x: M + 26, w: 250 },
    { k: "Qty", x: M + 330, align: "right" },
    { k: "Unit ex GST", x: M + 410, align: "right" },
    { k: "Total ex GST", x: R - 6, align: "right" },
  ];
  const tableHead = (y) => {
    doc.setFillColor(...NAVY); doc.rect(M, y, R - M, 20, "F");
    font("bold", 7.5); doc.setTextColor(255, 255, 255);
    cols.forEach((col) => doc.text(col.k.toUpperCase(), col.x, y + 13, col.align ? { align: col.align } : undefined));
    return y + 20;
  };

  header();
  let y = 122;
  const billRows = [tidy(o.billTo.company), o.billTo.contact, ...(o.billTo.address ?? []).map(tidy), o.billTo.email, o.billTo.phone];
  const delRows = o.deliverTo ? [o.deliverTo.name ? tidy(o.deliverTo.name) : null, ...(o.deliverTo.address ?? []).map(tidy)] : ["As per your order"];
  y = Math.max(block(M, y, "Invoice to", billRows, 240), block(320, y, "Delivered to", delRows, 235)) + 10;
  y = tableHead(y);

  t.lines.forEach((l, i) => {
    const desc = doc.splitTextToSize(l.description, cols[1].w);
    const rowH = 10 + desc.length * 10.5 + 10;
    if (y + rowH > H - 70) { footer(); doc.addPage(); page++; header(); y = tableHead(122); }
    if (i % 2 === 1) { doc.setFillColor(...LIGHT); doc.rect(M, y, R - M, rowH, "F"); }
    font("normal", 9); color(DARK);
    doc.text(String(i + 1), cols[0].x, y + 13);
    doc.text(desc, cols[1].x, y + 13);
    font("italic", 7.5); color(GRAY); doc.text(`Part no. ${l.code}`, cols[1].x, y + 13 + desc.length * 10.5);
    font("normal", 9); color(DARK);
    doc.text(l.qty.toLocaleString("en-AU"), cols[2].x, y + 13, { align: "right" });
    doc.text(unitMoney(l.unit), cols[3].x, y + 13, { align: "right" });
    font("bold", 9); doc.text(money(l.total), cols[4].x, y + 13, { align: "right" });
    y += rowH;
  });
  doc.setDrawColor(...RULE); doc.line(M, y, R, y);

  // Totals
  if (y > H - 230) { footer(); doc.addPage(); page++; header(); y = 122; }
  y += 16;
  const tot = [
    // Freight is always shown, even at $0.00, so the customer sees the free delivery.
    ["Subtotal ex GST", money(t.goods)],
    ["Freight ex GST", money(t.freight)],
    ["Total ex GST", money(t.subtotal)],
    ["GST", money(t.gst)],
    ["Total inc GST", money(t.total), true],
    o.paidByCard ? ["Paid by card", money(t.total)] : null,
    ["Amount due", o.paidByCard ? money(0) : money(t.total), true],
  ].filter(Boolean);
  tot.forEach(([k, v, bold]) => {
    font(bold ? "bold" : "normal", bold ? 10.5 : 9); color(bold ? DARK : GRAY);
    doc.text(k, R - 120, y, { align: "right" }); color(DARK); doc.text(v, R, y, { align: "right" });
    y += bold ? 16 : 13;
  });

  y += 14;
  const b = o.bank;
  const payRows = o.paidByCard
    ? ["Paid in full securely by credit card."]
    : [`EFT to ${b.accountName}`, `Bank: ${b.bank}  ·  BSB ${b.bsb}  ·  Account ${b.account}`, `Please quote ${o.number}. Remittances: ${b.remittanceEmail}`];
  const payEnd = block(M, y, "How to pay", payRows, 260);
  // Terms, with the due date in bold underneath (account orders).
  let termEnd = block(320, y, "Terms", [o.paidByCard ? "Paid at order." : o.terms], 235);
  if (!o.paidByCard) { font("bold", 9); color(DARK); doc.text(`Due ${o.dueDate}`, 320, termEnd); termEnd += 11; }
  y = Math.max(payEnd, termEnd) + 8;

  footer();
  const n = doc.getNumberOfPages();
  for (let p = 1; p <= n; p++) { doc.setPage(p); font("normal", 7.5); color(GRAY); doc.text(`Page ${p} of ${n}`, R, H - 14, { align: "right" }); }
  return doc;
}
